import { useEffect, useState } from 'react';
import { Calendar, X } from 'lucide-react';
import { AppScreen, CheckboxRow, StatusBar } from './kit';
import { PhoneFrame } from './PhoneFrame';
import { prefersReducedMotion, useInView } from './useInView';
import './ExportsMotionDemo.css';

const CHECKED_RECORDS = [
  'Chat Messages',
  'Expenses & Payments',
  'Custody Calendar',
  'Location Check-ins',
  'Parenting Time Compliance Report',
];

const UNCHECKED_RECORDS = [
  'Expense Dispute Summary',
  'Hearing Prep Brief',
  'Journal entries (private — yours only)',
];

const CHECK_START_MS = 520;
const CHECK_STAGGER_MS = 180;

export function ExportsMotionDemo() {
  const { ref, seen } = useInView<HTMLDivElement>({ threshold: 0.3 });
  const [reduced] = useState(() => prefersReducedMotion());
  const [checkedCount, setCheckedCount] = useState(
    reduced ? CHECKED_RECORDS.length : 0,
  );

  const active = seen || reduced;

  useEffect(() => {
    if (!active) return;

    if (reduced) {
      setCheckedCount(CHECKED_RECORDS.length);
      return;
    }

    setCheckedCount(0);
    const timers = CHECKED_RECORDS.map((_, index) =>
      window.setTimeout(
        () => setCheckedCount(index + 1),
        CHECK_START_MS + index * CHECK_STAGGER_MS,
      ),
    );

    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [active, reduced]);

  const className = [
    'exports-demo',
    active ? 'is-active' : '',
    reduced ? 'is-reduced' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div ref={ref} className={className}>
      <PhoneFrame size='md'>
        <AppScreen size='md'>
          <div className='exports-stage'>
            <StatusBar />

            <div className='exports-parent' aria-hidden='true'>
              <h2 className='exports-parent__title'>Settings</h2>
              <p className='exports-parent__sub'>
                Manage your account and preferences
              </p>
            </div>

            <div className='exports-scrim' aria-hidden='true' />

            <section
              className='exports-sheet'
              aria-labelledby='exports-sheet-title'
            >
              <div className='exports-handle' aria-hidden='true' />

              <header className='exports-head'>
                <h3 className='exports-title' id='exports-sheet-title'>
                  Generated Court-Ready Report
                </h3>
                <button
                  type='button'
                  className='exports-close'
                  aria-label='Close'
                  tabIndex={-1}
                >
                  <X size={15} strokeWidth={2.4} />
                </button>
              </header>

              <p className='exports-label'>EMAIL ADDRESS</p>
              <div className='exports-input'>jake.screenshots@example.com</div>

              <div className='exports-records-head'>
                <p className='exports-label exports-label--inline'>
                  RECORDS TO INCLUDE
                </p>
                <span className='exports-count'>{checkedCount} selected</span>
              </div>

              <div className='exports-checks'>
                {CHECKED_RECORDS.map((label, index) => (
                  <CheckboxRow
                    key={label}
                    checked={index < checkedCount}
                    label={label}
                  />
                ))}
                {UNCHECKED_RECORDS.map((label) => (
                  <CheckboxRow key={label} checked={false} label={label} />
                ))}
              </div>

              <p className='exports-label'>DATE RANGE (optional)</p>
              <div className='exports-dates'>
                <div className='exports-date'>
                  <Calendar size={14} strokeWidth={2} aria-hidden='true' />
                  <span>Start date</span>
                </div>
                <div className='exports-date'>
                  <Calendar size={14} strokeWidth={2} aria-hidden='true' />
                  <span>End date</span>
                </div>
              </div>

              <button type='button' className='exports-send' tabIndex={-1}>
                Send report
              </button>
            </section>
          </div>
        </AppScreen>
      </PhoneFrame>
    </div>
  );
}
